import clsx from 'clsx';
import { forwardRef, type PropsWithChildren, type Ref, type JSX } from 'react';

import { DataTableDefaultCell } from './components/DataTableDefaultCell.js';
import { DataTableEmptyState } from './components/DataTableEmptyState.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Grid } from '../../layouts/grid/Grid.js';
import { useDataTableConfigElement } from '../hooks/useDataTableConfig.js';
import { DataTableVirtualizationContainer } from './components/DataTableVirtualizationContainer.js';
import { DataTableSelectedRowsActions } from './components/TableActions/DataTableSelectedRowsActions.js';
import { DataTableSelectionChip } from './components/TableActions/DataTableSelectionChip.js';
import { DataTableTableActions } from './components/TableActions/DataTableTableActions.js';
import { DataTableTableActionsWrapper } from './components/TableActions/DataTableTableActionsWrapper.js';
import { DataTableColumnOrderSettings } from './components/Toolbar/ColumnSettings/DataTableColumnOrderSettings.js';
import { DataTableColumnPinningSettings } from './components/Toolbar/ColumnSettings/DataTableColumnPinningSettings.js';
import { DataTableColumnSettingsModal } from './components/Toolbar/ColumnSettings/DataTableColumnSettingsModal.js';
import { DataTableColumnSettingsTrigger } from './components/Toolbar/ColumnSettings/DataTableColumnSettingsTrigger.js';
import { DataTableColumnVisibilitySettings } from './components/Toolbar/ColumnSettings/DataTableColumnVisibilitySettings.js';
import { DataTableToolbar } from './components/Toolbar/DataTableToolbar.js';
import {
  DataTableColumnSettingsContextProvider,
  DataTableContextProvider,
} from './contexts/DataTableContext.js';
import { getCoreRowModel } from './core-row-model.js';
import * as styles from './DataTable.sty.js';
import { useColumnFontStyle } from './features/ColumnFontStyle/ColumnFontStyle.js';
import { useColumnOrder } from './features/ColumnOrder/ColumnOrder.js';
import { useColumnPinning } from './features/ColumnPinning/ColumnPinning.js';
import { useColumnSizing } from './features/ColumnSizing/ColumnSizing.js';
import { useColumnVisibility } from './features/ColumnVisibility/ColumnVisibility.js';
import { useDataTableConfigRef } from './features/Config.js';
import { _getDevToolsSlot, DataTableDevTools } from './features/DevTools.js';
import {
  DataTableDownload,
  useDownloadData,
} from './features/Download/Download.js';
import { useGanttChart } from './features/GanttChart/GanttChart.js';
import { useLayout } from './features/Layout/Layout.js';
import {
  DataTableGlobalLineWrapAction,
  useColumnLineWrap,
} from './features/LineWrap/LineWrap.js';
import { useLoadingState } from './features/LoadingState/LoadingState.js';
import { useMeterBarChartMaxValuesCalc } from './features/MeterBarChart/MeterBarChart.js';
import {
  DataTablePagination,
  usePagination,
} from './features/Pagination/Pagination.js';
import { DataTablePaginationInternal } from './features/Pagination/PaginationInternal.js';
import {
  DataTableExpandableRowTemplate,
  DataTableExpandableRowWrapper,
  useRowDetails,
} from './features/RowDetails/RowDetails.js';
import { useRowIdentification } from './features/RowIdentification/RowIdentification.js';
import { useRowInteractivity } from './features/RowInteractivity/RowInteractivity.js';
import { useRowOrder } from './features/RowOrder/RowOrder.js';
import { useRowSelection } from './features/RowSelection/RowSelection.js';
import { getSortedRowModel } from './features/Sorting/sorted-row-model.js';
import { useSorting } from './features/Sorting/Sorting.js';
import { useSubRows } from './features/SubRows/SubRows.js';
import * as thresholdStyles from './features/Thresholds/Thresholds.sty.js';
import { useThresholds } from './features/Thresholds/Thresholds.js';
import { DataTableCellActions } from './features/UserActions/CellActions.js';
import { DataTableColumnActions } from './features/UserActions/ColumnActions.js';
import { DataTableRowActions } from './features/UserActions/RowActions.js';
import { useUserActions } from './features/UserActions/useUserActions.js';
import { useVisualVariant } from './features/VisualVariants/VisualVariants.js';
import { useBuiltInColumns } from './hooks/useBuiltInColumns.js';
import { useColumnRemeasure } from './hooks/useColumnRemeasure.js';
import { useColumns } from './hooks/useColumns.js';
import { useDataTableSlots } from './hooks/useDataTableSlots.js';
import { usePaginationEmptyPageCorrection } from './hooks/usePaginationEmptyPageCorrection.js';
import { usePaginationSlot } from './hooks/usePaginationSlot.js';
import type {
  CoreOptions,
  TableOptions,
  FeatureOptions,
} from './hooks/useTable/types.js';
import { useTable } from './hooks/useTable/useTable.js';
import { useTableRootResize } from './hooks/useTableRootResize.js';
import { useToolbarSlot } from './hooks/useToolbarSlot.js';
import type { DataTableProps, DataTableRef } from './public.api.js';
import { getExpandedRowModel } from './tanstack-table-core-override/src/index.js';
import { Intent as CoreIntent } from '../../core/slots/Intent/Intent.js';
import { extractDataAttributes } from '../utils/extract-data-attributes.js';

const COMPONENT_NAME = 'DataTable';

function InternalDataTable<TData, TValue>(
  props: PropsWithChildren<DataTableProps<TData>>,
  forwardedRef: Ref<DataTableRef>,
): JSX.Element {
  const {
    children,
    data: dataProps,
    columns: columnsProps,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const dataAttributes = extractDataAttributes(restProps);

  const slots = useDataTableSlots(children);
  const {
    toolbarSlots,
    toolbarContent,
    toolbarItemProps,
    toolbarRemainingProps,
    openColumnSettings,
    columnSettingsModalRef,
  } = useToolbarSlot(slots.toolbarProps);
  const {
    paginationConsumerId,
    paginationDataTestId,
    paginationConsumerStyle,
    paginationConsumerClassName,
    paginationDataAttributes,
  } = usePaginationSlot(slots.pagination);

  // Hook used to adapt column definition for internal usage
  const [columns, consumerColumnDefMap] = useColumns<TData, TValue>(
    columnsProps,
  );

  const configFullWidthOption = useDataTableConfigElement('fullWidth');
  const fullWidth = props.fullWidth ?? configFullWidthOption;

  const configFullHeightOption = useDataTableConfigElement('fullHeight');
  const fullHeight = props.fullHeight ?? configFullHeightOption;

  const options: TableOptions<TData> = {
    columns,
    data: dataProps,
    // In order to keep the types in our own realm, the cast to align with our
    // CoreOptions is necessary.
    getCoreRowModel:
      getCoreRowModel() as unknown as CoreOptions<TData>['getCoreRowModel'],
    getSortedRowModel: getSortedRowModel(),
    // In order to keep the types in our own realm, the cast to align with our
    // CoreOptions is necessary.
    getExpandedRowModel:
      getExpandedRowModel() as unknown as FeatureOptions<TData>['getExpandedRowModel'],
  };

  useRowIdentification(props, options);
  useLayout(props, options);
  useColumnLineWrap(props, options);
  useColumnFontStyle(props, options);
  useThresholds(props, options);
  useVisualVariant(props, options);
  useUserActions<TData>(props, options, consumerColumnDefMap);
  usePagination(props, options);
  useLoadingState(props, options);
  useColumnSizing(props, options);
  useSorting(props, options);
  useColumnVisibility(props, options);
  useColumnOrder(props, options);
  useRowSelection(props, options);
  useSubRows(props, options);
  useRowDetails(props, options);
  useRowInteractivity(props, options);
  useGanttChart<TData, TValue>(props, options);
  useMeterBarChartMaxValuesCalc(props, options);
  useRowOrder(props, options);
  useColumnPinning(props, options);
  // DEPRECATION: [APPDEV-16236] BREAKING-CHANGE: Remove deprecated onDownloadData and DataTableDownloadProps.
  // After removing the deprecated prop, consider changing the signature to `useDownloadData(toolbarItemProps, options)`
  useDownloadData(
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    { onDownloadData: toolbarItemProps.onDownloadData ?? props.onDownloadData },
    options,
  );

  // Adds the builtin columns depending on which features are enabled
  options.columns = useBuiltInColumns(options);
  const table = useTable<TData>(options);

  const tableRef = useDataTableConfigRef(
    forwardedRef,
    columnSettingsModalRef,
    table,
    props,
  );
  const rootRef = useTableRootResize(table.remeasureRows, table.setState);
  const mergedRefs = useMergeRefs([rootRef, tableRef]);

  useColumnRemeasure<TData>(table);
  usePaginationEmptyPageCorrection<TData>(table);

  // Gets the passed ReactTableDevtools in order to make debugging easier.
  const DevTools = _getDevToolsSlot(props, table);

  return (
    <>
      <div
        ref={mergedRefs}
        className={clsx(
          styles.dataTable,
          {
            [styles.fullWidth]: fullWidth,
            [styles.notResizing]:
              table.getState().columnSizingInfo.isResizingColumn === false,
          },
          {
            [thresholdStyles.hasPillThresholds]:
              table.options.hasThresholds &&
              table.options.rowThresholds?.some(
                (threshold) => threshold.type === 'pill',
              ),
          },
          consumerClassName,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...dataAttributes}
        data-dt-component={COMPONENT_NAME}
      >
        <DataTableContextProvider value={table}>
          {(toolbarContent ||
            slots.tableActions ||
            slots.selectedRowsActions) && (
            <Grid
              gridTemplateColumns="1fr auto"
              paddingBottom={4}
              gap={toolbarContent ? 8 : 0}
            >
              <DataTableTableActionsWrapper
                tableActions={slots.tableActions}
                selectedRowsActions={slots.selectedRowsActions}
              />
              {/* We need to actually render the toolbar here to trigger rerenders once the table context updates */}
              <DataTableColumnSettingsContextProvider
                value={{ openColumnSettings }}
              >
                <DataTableToolbar {...toolbarRemainingProps}>
                  {toolbarContent}
                </DataTableToolbar>
              </DataTableColumnSettingsContextProvider>
            </Grid>
          )}
          <DataTableColumnSettingsModal
            ref={columnSettingsModalRef}
            visibilitySettings={toolbarSlots.visibilitySettings}
            columnOrderSettings={toolbarSlots.columnOrderSettings}
            columnPinningSettings={toolbarSlots.columnPinningSettings}
            resetColumnVisibility={toolbarItemProps.resetColumnVisibility}
            resetColumnOrder={toolbarItemProps.resetColumnOrder}
            resetColumnPinning={toolbarItemProps.resetColumnPinning}
          />

          <DataTableVirtualizationContainer
            table={table}
            emptyState={slots.emptyState}
            fullHeight={fullHeight ?? false}
          />

          {table.options.enablePagination === true ? (
            <DataTablePaginationInternal
              id={paginationConsumerId}
              data-testid={paginationDataTestId}
              className={paginationConsumerClassName}
              style={paginationConsumerStyle}
              {...paginationDataAttributes}
            />
          ) : null}
        </DataTableContextProvider>
      </div>
      {DevTools}
    </>
  );
}

InternalDataTable.displayName = COMPONENT_NAME;

const DataTableFwd = /* @__PURE__ */ forwardRef(InternalDataTable);

/**
 * The `DataTable` is a component for building tables which organize information
 * into rows and columns.
 * @public
 */
export const DataTable = Object.assign(DataTableFwd, {
  Pagination: DataTablePagination,
  ColumnActions: DataTableColumnActions,
  CellActions: DataTableCellActions,
  RowActions: DataTableRowActions,
  SelectedRowsActions: DataTableSelectedRowsActions,
  DefaultCell: DataTableDefaultCell,
  EmptyState: DataTableEmptyState,
  TableActions: DataTableTableActions,
  DevTools: DataTableDevTools,
  Toolbar: DataTableToolbar,
  VisibilitySettings: DataTableColumnVisibilitySettings,
  ColumnOrderSettings: DataTableColumnOrderSettings,
  ColumnPinningSettings: DataTableColumnPinningSettings,
  ColumnSettingsTrigger: DataTableColumnSettingsTrigger,
  SelectionChip: DataTableSelectionChip,
  DownloadData: DataTableDownload,
  Intent: CoreIntent,
  ExpandableRow: DataTableExpandableRowTemplate,
  ExpandableRowWrapper: DataTableExpandableRowWrapper,
  LineWrap: DataTableGlobalLineWrapAction,
});
